/**
 * 上报管理器
 * 职责：统一管理数据上报，支持双写、重试、离线缓存
 */

import { Buffer } from "../buffer/Buffer";
import { TrackEvent } from "../event/EventBus";
import { TrackerLogger } from "../utils/logger";

export interface ReporterOptions {
  project?: string; // 项目变量（用于区分数据源，默认: hb_web_test）
  enableLegacy?: boolean; // 是否同时上报到旧架构
  enableBatch?: boolean; // 是否启用批量上报
  enableRetry?: boolean; // 是否启用重试
  enableOfflineCache?: boolean; // 是否启用离线缓存
  batchSize?: number;
  batchDelay?: number;
  maxRetries?: number;
}

export class Reporter {
  private buffer: Buffer | null = null;
  private options: Required<ReporterOptions>;
  private offlineQueue: TrackEvent[] = [];
  private retryQueue: Array<{ event: TrackEvent; retries: number }> = [];
  private isOnline: boolean = true;

  constructor(options: ReporterOptions = {}) {
    this.options = {
      project: options.project ?? "hb_web_test", // 默认使用测试项目变量
      enableLegacy: options.enableLegacy ?? false,
      enableBatch: options.enableBatch ?? true,
      enableRetry: options.enableRetry ?? true,
      enableOfflineCache: options.enableOfflineCache ?? true,
      batchSize: options.batchSize ?? 30,
      batchDelay: options.batchDelay ?? 3000,
      maxRetries: options.maxRetries ?? 3,
    };

    if (this.options.enableBatch) {
      this.buffer = new Buffer((events) => this.flushBatch(events), {
        size: this.options.batchSize,
        delay: this.options.batchDelay,
      });
    }

    // 监听在线/离线状态
    if (typeof window !== "undefined" && this.options.enableOfflineCache) {
      this.isOnline = navigator.onLine;

      window.addEventListener("online", () => {
        this.isOnline = true;
        TrackerLogger.info(
          "Reporter",
          "Network online, flushing offline queue"
        );
        this.flushOfflineQueue();
      });

      window.addEventListener("offline", () => {
        this.isOnline = false;
        TrackerLogger.info("Reporter", "Network offline");
      });
    }

    // 页面卸载时上报
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.flush();
      });
    }

    // 从 localStorage 加载离线队列
    if (this.options.enableOfflineCache) {
      this.loadOfflineQueue();
    }

    TrackerLogger.info("Reporter", "Initialized with options:", this.options);
  }

  /**
   * 上报单个事件
   */
  report(event: TrackEvent): void {
    // 添加到缓冲区（如果启用）
    if (this.buffer) {
      this.buffer.add(event);
      return;
    }

    // 直接上报
    this.reportSingle(event);
  }

  /**
   * 上报单个事件（内部方法）
   */
  private reportSingle(event: TrackEvent): void {
    // 检查是否在线
    if (!this.isOnline && this.options.enableOfflineCache) {
      this.offlineQueue.push(event);
      this.saveOfflineQueue();
      TrackerLogger.report("offline", event.event_name);
      return;
    }

    try {
      // 构建上报数据
      const reportData = {
        event_id: event.event_id,
        event_name: event.event_name,
        ...event.data,
        _tracker_version: "v2", // 标记为新架构
        _tracker_project: this.options.project, // 项目变量（用于双线上报区分）
        _tracker_session_id: event.metadata?.sessionId,
        _tracker_trace_id: event.metadata?.traceId,
        _tracker_timestamp: event.timestamp,
      };

      // 使用 SDK 的上报方法
      // 通过 scopeObject.basic.project 设置外层 project 为 hb_web_test
      if (typeof window !== "undefined" && (window as any).gdTracker) {
        const { trackerWind } = (window as any).gdTracker;

        // scopeObject 用于设置各层级的数据
        // basic: 最外层字段（包括 project）
        // common: data 字段下的数据
        // event: attributes 字段下的数据
        const scopeObject: any = {
          basic: {
            project: this.options.project, // 设置外层 project 为 hb_web_test
          },
        };

        // 注意：新版埋点不覆盖 SDK 自动生成的 uk_id
        // 使用 trackEvent 的第三个参数传入 scopeObject
        trackerWind.trackEvent(event.event_name, reportData, scopeObject);

        TrackerLogger.info(
          "Reporter",
          `Event reported: ${event.event_name} (project: ${this.options.project})`
        );
      } else if (typeof window !== "undefined" && (window as any).reporter) {
        // 降级：使用旧的 reporter 函数
        (window as any).reporter(reportData);
        TrackerLogger.report("send", event.event_name, "legacy reporter");
      } else {
        // SDK 和旧版 reporter 都不存在，放弃上报
        TrackerLogger.warn(
          "Reporter",
          "SDK and legacy reporter not found, skipping:",
          event.event_name
        );
        // 不上报，避免调用不存在的 API
      }

      // 双写：同时上报到旧架构（如果启用）
      if (this.options.enableLegacy) {
        TrackerLogger.info(
          "Reporter",
          "Dual write enabled (legacy report skipped in v2)"
        );
      }
    } catch (error) {
      TrackerLogger.error("Reporter", "Report error:", error);

      // 重试机制
      if (this.options.enableRetry) {
        this.retryQueue.push({ event, retries: 0 });
        this.processRetryQueue();
      }

      // 离线缓存
      if (this.options.enableOfflineCache && !this.isOnline) {
        this.offlineQueue.push(event);
        this.saveOfflineQueue();
      }
    }
  }

  /**
   * 批量上报
   */
  private flushBatch(events: TrackEvent[]): void {
    if (events.length === 0) return;

    // 按事件类型分组
    const grouped = this.groupByEventType(events);

    // 分别上报每组事件
    Object.entries(grouped).forEach(([eventName, eventList]) => {
      if (eventList.length === 1) {
        // 单个事件直接上报
        this.reportSingle(eventList[0]);
      } else {
        // 批量事件合并上报
        eventList.forEach((event) => this.reportSingle(event));
      }
    });

    TrackerLogger.report("flush", undefined, `${events.length} events`);
  }

  /**
   * 立即上报缓冲区中的所有事件
   */
  flush(): void {
    if (this.buffer) {
      this.buffer.flush();
    }
    this.flushOfflineQueue();
  }

  /**
   * 上报离线队列
   */
  private flushOfflineQueue(): void {
    if (this.offlineQueue.length === 0) return;
    if (!this.isOnline) return;

    const events = [...this.offlineQueue];
    this.offlineQueue = [];

    TrackerLogger.report(
      "flush",
      undefined,
      `Offline queue: ${events.length} events`
    );

    events.forEach((event) => {
      this.reportSingle(event);
    });

    this.clearOfflineQueue();
  }

  /**
   * 处理重试队列
   */
  private processRetryQueue(): void {
    if (this.retryQueue.length === 0) return;

    const toRetry = [...this.retryQueue];
    this.retryQueue = [];

    toRetry.forEach(({ event, retries }) => {
      if (retries >= this.options.maxRetries) {
        TrackerLogger.error(
          "Reporter",
          "Max retries reached for event:",
          event.event_name
        );
        return;
      }

      setTimeout(() => {
        try {
          this.reportSingle(event);
        } catch (error) {
          this.retryQueue.push({ event, retries: retries + 1 });
        }
      }, Math.pow(2, retries) * 1000); // 指数退避
    });
  }

  /**
   * 保存离线队列到 localStorage
   */
  private saveOfflineQueue(): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          "tracker_v2_offline_queue",
          JSON.stringify(this.offlineQueue)
        );
      }
    } catch (error) {
      TrackerLogger.error("Reporter", "Save offline queue error:", error);
    }
  }

  /**
   * 从 localStorage 加载离线队列
   */
  private loadOfflineQueue(): void {
    try {
      if (typeof localStorage !== "undefined") {
        const data = localStorage.getItem("tracker_v2_offline_queue");
        if (data) {
          this.offlineQueue = JSON.parse(data);

          TrackerLogger.info(
            "Reporter",
            `Loaded offline queue: ${this.offlineQueue.length} events`
          );

          // 如果在线，立即上报
          if (this.isOnline) {
            this.flushOfflineQueue();
          }
        }
      }
    } catch (error) {
      TrackerLogger.error("Reporter", "Load offline queue error:", error);
    }
  }

  /**
   * 清空离线队列
   */
  private clearOfflineQueue(): void {
    this.offlineQueue = [];
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("tracker_v2_offline_queue");
      }
    } catch (error) {
      TrackerLogger.error("Reporter", "Clear offline queue error:", error);
    }
  }

  /**
   * 按事件类型分组
   */
  private groupByEventType(events: TrackEvent[]): Record<string, TrackEvent[]> {
    return events.reduce((acc, event) => {
      const key = event.event_name;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(event);
      return acc;
    }, {} as Record<string, TrackEvent[]>);
  }
}

// 导出单例
export const reporter = new Reporter({
  project: "hb_web_test", // 新架构使用 hb_web_test 项目变量进行双线上报
  enableLegacy: false, // 默认不启用双写，按需开启
  enableBatch: false, // 暂时禁用批量上报，待服务端升级后开启
  enableRetry: true,
  enableOfflineCache: true,
});
